/* eslint-disable no-console */
type LogLevel = 'info' | 'warn' | 'error' | 'debug';

type LoggerLike = {
  info: (msg: string, meta?: Record<string, unknown>) => void;
  warn: (msg: string, meta?: Record<string, unknown>) => void;
  error: (msg: string, meta?: Record<string, unknown>) => void;
  debug: (msg: string, meta?: Record<string, unknown>) => void;
};

const send = (level: LogLevel, message: string, meta?: Record<string, unknown>) => {
  try {
    fetch('/api/logs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({ level, message, meta }),
    });
  } catch {
    // ignore fire-and-forget network errors
    return;
  }
};

const browserLogger: LoggerLike = {
  info: (m, meta) => {
    console.info(m, meta ?? {});
    send('info', m, meta);
  },
  warn: (m, meta) => {
    console.warn(m, meta ?? {});
    send('warn', m, meta);
  },
  error: (m, meta) => {
    console.error(m, meta ?? {});
    send('error', m, meta);
  },
  debug: (m, meta) => {
    console.debug(m, meta ?? {});
    if (process.env.NODE_ENV !== 'production') send('debug', m, meta);
  },
};

export default browserLogger;

/* ------------------------------------------------------------------ */
/*  Logger à namespace (console navigateur stylée)                    */
/* ------------------------------------------------------------------ */

const isDev = process.env.NODE_ENV !== 'production';

/**
 * Couleur stable dérivée du nom du namespace (hash → teinte HSL). Deux modules
 * différents ont ainsi toujours un badge de couleur distincte et reproductible.
 */
function hueFor(ns: string): number {
  let hash = 0;
  for (let i = 0; i < ns.length; i += 1) hash = (hash * 31 + ns.charCodeAt(i)) % 360;
  return hash;
}

const LEVEL_STYLE: Record<LogLevel, string> = {
  debug: 'color:#64748b',
  info: 'color:#0ea5e9',
  warn: 'color:#d97706',
  error: 'color:#dc2626',
};

/**
 * Crée un logger nommé pour le navigateur. Chaque module obtient un badge coloré
 * stable, ce qui rend la console filtrable et lisible sans multiplier les
 * `console.log` bruts.
 *
 * ```ts
 * const log = createLogger('Timesheet');
 * log.info('CRA soumis', { month: '2026-07' });
 * log.error('échec de soumission', { status: 500 });
 * ```
 *
 * - `debug` est silencieux en production.
 * - `warn` et `error` sont aussi remontés au serveur (`/api/logs`) pour être
 *   visibles dans le terminal, comme les autres logs serveur.
 */
export function createLogger(namespace: string): LoggerLike {
  const badge = `background:hsl(${hueFor(namespace)} 70% 45%);color:#fff;border-radius:4px;padding:1px 6px;font-weight:600`;

  const emit = (level: LogLevel, message: string, meta?: Record<string, unknown>) => {
    if (level === 'debug' && !isDev) return;
    const fn = level === 'debug' ? console.debug : console[level];
    const args: unknown[] = [`%c${namespace}%c ${message}`, badge, LEVEL_STYLE[level]];
    if (meta && Object.keys(meta).length) args.push(meta);
    fn(...args);
    if (level === 'warn' || level === 'error') send(level, `[${namespace}] ${message}`, meta);
  };

  return {
    debug: (m, meta) => emit('debug', m, meta),
    info: (m, meta) => emit('info', m, meta),
    warn: (m, meta) => emit('warn', m, meta),
    error: (m, meta) => emit('error', m, meta),
  };
}
